import { useState } from 'react';

interface Props {
  /** Resolves true when the backend accepted the batch — the form then clears. */
  onSubmit: (text: string, reason: string) => Promise<boolean>;
}

const PLACEHOLDER = ['player-1001', 'player-1002', 'qa-bot-7'].join('\n');

export function AddTestPlayersForm({ onSubmit }: Props) {
  const [text, setText] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const empty = text.trim() === '';

  async function handleSubmit() {
    if (empty || submitting) return;
    setSubmitting(true);
    try {
      const accepted = await onSubmit(text, reason);
      if (accepted) {
        setText('');
        setReason('');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mb-5 max-w-2xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4">
      <h2 className="mb-1 text-[13px] font-semibold text-[var(--co-text)]">Add test players</h2>
      <p className="mb-3 text-[11px] text-[var(--co-muted)]">
        One player id per line — commas and semicolons work too, and a pasted CSV with an
        account_id column is accepted. An id that is already active stays as it is; a
        deactivated one starts being excluded again.
      </p>

      <label
        htmlFor="test-player-ids"
        className="mb-1 block text-[11px] font-medium text-[var(--co-muted)]"
      >
        Player ids
      </label>
      <textarea
        id="test-player-ids"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={PLACEHOLDER}
        rows={5}
        disabled={submitting}
        className="mb-3 w-full rounded-lg border border-[var(--co-border)] bg-transparent px-3 py-2 font-mono text-[12px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)]"
      />

      <label
        htmlFor="test-player-reason"
        className="mb-1 block text-[11px] font-medium text-[var(--co-muted)]"
      >
        Reason (optional)
      </label>
      <input
        id="test-player-reason"
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Internal QA accounts"
        maxLength={255}
        disabled={submitting}
        className="mb-3 w-full rounded-lg border border-[var(--co-border)] bg-transparent px-3 py-2 text-[13px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)]"
      />

      <button
        type="button"
        onClick={() => void handleSubmit()}
        disabled={empty || submitting}
        className="rounded-lg bg-[var(--co-brand)] px-4 py-2 text-[12px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? 'Adding…' : 'Add to registry'}
      </button>
    </div>
  );
}
